import type { AccountFacilitiesFetchFailedAction } from '@Application/Facilities/Facilities/Actions/accountFacilitiesFetchFailed';
import type { OrganizationsFetchFailedAction } from '@Application/Organizations/Organizations/Actions/organizationsFetchFailed';
import FetchAccountFacilities from '@Application/Facilities/Facilities/Queries/FetchAccountFacilities';
import fetchAccountFacilities from '@Application/Facilities/Facilities/fetchAccountFacilities';
import FetchOrganizations from '@Application/Organizations/Organizations/Queries/FetchOrganizations';
import fetchOrganizations from '@Application/Organizations/Organizations/fetchOrganizations';
import readCurrentFacility from '@Application/Facilities/CurrentFacility/readCurrentFacility';
import readCurrentOrganization from '@Application/Organizations/CurrentOrganization/readCurrentOrganization';
import recordCurrentOrganization from '@Application/Organizations/CurrentOrganization/recordCurrentOrganization';
import useAuthenticatedUser from '@UI/Providers/AuthenticatedUserProvider/useAuthenticatedUser';
import CurrentOrganizationContext, {
	type CurrentOrganizationContextValue,
	type StartupError,
} from '@UI/Providers/CurrentOrganizationProvider/CurrentOrganizationContext';
import composeAssignedOrganizations, {
	type ComposedOrganization,
} from '@UI/Providers/CurrentOrganizationProvider/helpers/composeAssignedOrganizations';
import * as React from 'react';
import { useDispatch, useSelector, useStoreQuery } from '@verclam/synapse';

type Props = {
	readonly children: React.ReactNode;
};

/**
 * Mounted below `AuthenticatedUserProvider`, above `CurrentFacilityProvider` (inside `AppShell`).
 * Fetches organizations and facilities as two independent, parallel requests, then composes them
 * client-side (`composeAssignedOrganizations`) into the grouping `CurrentFacilityProvider` and the
 * header switcher both read from here — there is no longer a single payload that carries both.
 *
 * Both `stopOn` compare the resolved view's own `accountId` against the current one: `organizationsStore`
 * is shared with the organizations administration screen, and `accountFacilitiesStore` could just as
 * well be repopulated for a different account across a `USER_REVOKED` boundary — an unqualified
 * `value !== null` would settle on a list that belongs to someone else.
 */
const CurrentOrganizationProvider: React.FC<Props> = ({ children }) => {
	const dispatch = useDispatch();
	const { user, sessionStatus } = useAuthenticatedUser();
	const accountId = user?.account?.id ?? null;
	const canFetch = sessionStatus === 'authenticated' && accountId !== null;

	const [currentOrganizationId, setCurrentOrganizationId] = React.useState<number | null>(null);

	/**
	 * `useStoreQuery` converts whatever its `query` rejects with into a bare `Error` (losing the HTTP
	 * status in the process), so the status has to be captured here, in the `.catch` attached to the
	 * dispatched thunk, which still receives the raw `*_FETCH_FAILED` action. Reset at the start of every
	 * attempt so a retry that succeeds does not leave a stale failure behind.
	 */
	const [organizationsFailure, setOrganizationsFailure] = React.useState<{ status: number | null } | null>(
		null,
	);
	const [facilitiesFailure, setFacilitiesFailure] = React.useState<{ status: number | null } | null>(null);

	const { data: organizationsListView, error: organizationsError } = useStoreQuery({
		selector: (state) => state.organizationsStore.organizations,
		stopOn: (value) => value !== null && value.accountId === accountId,
		query: () => {
			if (!canFetch || accountId === null) {
				return Promise.resolve();
			}

			setOrganizationsFailure(null);

			return dispatch(fetchOrganizations(new FetchOrganizations(accountId))).catch(
				(action: OrganizationsFetchFailedAction) => {
					setOrganizationsFailure({ status: action.status });
					throw action;
				},
			);
		},
		deps: [accountId],
	});

	// `useStoreQuery` hides its data while `stopOn` is false, which includes a pending refresh request:
	// the view is read directly so the list stays displayed during a re-read.
	const accountFacilitiesListView = useSelector((state) => state.accountFacilitiesStore.accountFacilities);
	const hasConfirmedFacilitiesRef = React.useRef(false);

	const { error: facilitiesError } = useStoreQuery({
		selector: (state) => state.accountFacilitiesStore.accountFacilities,
		stopOn: (value) => value !== null && value.accountId === accountId && !value.isRefreshRequested,
		query: () => {
			if (!canFetch || accountId === null) {
				return Promise.resolve();
			}

			setFacilitiesFailure(null);

			return dispatch(fetchAccountFacilities(new FetchAccountFacilities(accountId))).catch(
				(action: AccountFacilitiesFetchFailedAction) => {
					// A failed re-read keeps the confirmed list: only a startup failure is a diagnostic.
					if (!hasConfirmedFacilitiesRef.current) {
						setFacilitiesFailure({ status: action.status });
					}
					throw action;
				},
			);
		},
		deps: [accountId],
	});

	const areOrganizationsConfirmed =
		canFetch && organizationsListView !== null && organizationsListView.accountId === accountId;
	const areFacilitiesConfirmed =
		canFetch && accountFacilitiesListView !== null && accountFacilitiesListView.accountId === accountId;

	// `useStoreQuery`'s `error` is `Error | null`, never `undefined` — comparing against `undefined` here
	// would always be `true`, settling the provider before either fetch has actually answered.
	hasConfirmedFacilitiesRef.current = areFacilitiesConfirmed;

	const haveOrganizationsSettled = areOrganizationsConfirmed || organizationsError !== null;
	const haveFacilitiesSettled = areFacilitiesConfirmed || facilitiesError !== null;

	/**
	 * `!canFetch` is not decorative: a user with no account triggers neither fetch, and without this
	 * first term `isResolved` would stay `false` forever for them — an infinite spinner rather than the
	 * "no structure" message `AppMain` renders from `user.account === null`.
	 */
	const isResolved = !canFetch || (haveOrganizationsSettled && haveFacilitiesSettled);

	const startupError = React.useMemo<StartupError | null>(() => {
		if (organizationsFailure !== null) {
			return { scope: 'organizations', status: organizationsFailure.status };
		}

		if (facilitiesFailure !== null) {
			return { scope: 'facilities', status: facilitiesFailure.status };
		}

		return null;
	}, [organizationsFailure, facilitiesFailure]);

	const organizations = React.useMemo<readonly ComposedOrganization[]>(
		() =>
			composeAssignedOrganizations(
				organizationsListView?.list ?? [],
				accountFacilitiesListView?.list ?? [],
			),
		[organizationsListView, accountFacilitiesListView],
	);

	/**
	 * True only when the organizations fetch itself is a server payload confirming zero organizations —
	 * never when it is empty because there is no account to query, or because either fetch failed.
	 * Those synthetic cases also drive `organizations` to `[]` above and `isResolved` to `true`, but
	 * they carry no information about the caller's real organizations: they must stay distinguishable
	 * from a confirmed empty answer so `CurrentFacilityProvider` never purges a persisted facility, and
	 * so `AppMain` never claims the caller has no organization, on a transient error.
	 */
	const hasConfirmedNoOrganizations = areOrganizationsConfirmed && organizations.length === 0;

	/**
	 * Runs once both fetches resolve successfully, and again whenever the resolved organization stops
	 * being assigned (retired mid-session). Order: the persisted organization id, else the organization
	 * of the persisted facility id, else the first alphabetically. Unlike the facility's
	 * no-automatic-reselection rule, there is no dedicated organization picker — falling through to a
	 * sane default beats stalling on an unreachable `null`.
	 *
	 * The single guard below — rather than one per branch — closes the purge, the persisted-facility
	 * lookup and the alphabetical fallback all at once: running any of them off a facilities list that
	 * failed to load would compute (and persist) a wrong organization, not merely display a stale one.
	 */
	React.useEffect(() => {
		if (!areOrganizationsConfirmed || !areFacilitiesConfirmed) {
			return;
		}

		if (organizations.length === 0) {
			recordCurrentOrganization(null);
			setCurrentOrganizationId(null);
			return;
		}

		if (
			currentOrganizationId !== null &&
			organizations.some((organization) => organization.id === currentOrganizationId)
		) {
			return;
		}

		const persistedOrganizationId = readCurrentOrganization();

		if (
			persistedOrganizationId !== null &&
			organizations.some((organization) => organization.id === persistedOrganizationId)
		) {
			recordCurrentOrganization(persistedOrganizationId);
			setCurrentOrganizationId(persistedOrganizationId);
			return;
		}

		const persistedFacilityId = readCurrentFacility();
		const organizationOfPersistedFacility =
			persistedFacilityId !== null
				? organizations.find((organization) =>
						organization.facilities.some((facility) => facility.id === persistedFacilityId),
					)
				: undefined;

		if (organizationOfPersistedFacility !== undefined) {
			recordCurrentOrganization(organizationOfPersistedFacility.id);
			setCurrentOrganizationId(organizationOfPersistedFacility.id);
			return;
		}

		const firstOrganization =
			organizations.find((organization) => organization.facilities.length > 0) || organizations[0];

		if (firstOrganization === undefined) {
			return;
		}

		recordCurrentOrganization(firstOrganization.id);
		setCurrentOrganizationId(firstOrganization.id);
	}, [organizations, currentOrganizationId, areOrganizationsConfirmed, areFacilitiesConfirmed]);

	const currentOrganization = React.useMemo<ComposedOrganization | null>(() => {
		if (currentOrganizationId === null) {
			return null;
		}

		return organizations.find((organization) => organization.id === currentOrganizationId) ?? null;
	}, [organizations, currentOrganizationId]);

	const selectOrganization = React.useCallback((organizationId: number) => {
		recordCurrentOrganization(organizationId);
		setCurrentOrganizationId(organizationId);
	}, []);

	const value = React.useMemo<CurrentOrganizationContextValue>(
		() => ({
			currentOrganization,
			organizations,
			isResolved,
			hasConfirmedNoOrganizations,
			areOrganizationsConfirmed,
			areFacilitiesConfirmed,
			startupError,
			selectOrganization,
		}),
		[
			currentOrganization,
			organizations,
			isResolved,
			hasConfirmedNoOrganizations,
			areOrganizationsConfirmed,
			areFacilitiesConfirmed,
			startupError,
			selectOrganization,
		],
	);

	return (
		<CurrentOrganizationContext.Provider value={value}>{children}</CurrentOrganizationContext.Provider>
	);
};

export default CurrentOrganizationProvider;
